import { X } from "lucide-react";
import type { RefObject } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../../components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "../../../../../components/ui/dialog";
import { LabeledValue } from "../../../../../components/ui/labeledValue";

export interface OriginalRow {
  readonly label: string;
  readonly value?: string;
}

interface ProductOriginalsModalProperties {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly triggerRef: RefObject<HTMLButtonElement | null>;
  readonly unavailable?: boolean;
  /** Originally sold values (`contractVsc` / `contractGap`), view only. */
  readonly rows: readonly OriginalRow[];
}

/** View-only popup (P3 for VSC, P4 for GAP), available before and after submission. */
export function ProductOriginalsModal({
  open,
  onOpenChange,
  title,
  rows,
  triggerRef,
  unavailable = false,
}: ProductOriginalsModalProperties) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="max-h-[90vh] gap-0 overflow-y-auto p-0"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          triggerRef.current?.focus();
        }}
      >
        <div className="bg-primary text-primary-foreground flex items-center justify-between border-b px-4 py-2">
          <DialogTitle className="text-primary-foreground text-sm font-semibold">{title}</DialogTitle>
          <DialogDescription className="sr-only">{t("vehicle.productOriginals.description")}</DialogDescription>
          <DialogClose
            className="text-primary-foreground/80 hover:text-primary-foreground p-0.5 transition-colors"
            aria-label={t("vehicle.productOriginals.close")}
          >
            <X className="size-4.5" aria-hidden="true" />
          </DialogClose>
        </div>
        {unavailable ? (
          <p role="alert" className="text-warning p-4 text-sm">
            {t("vehicle.productOriginals.loadError")}
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 md:grid-cols-3">
            {rows.map((row) => (
              <LabeledValue
                key={row.label}
                label={row.label}
                value={row.value || t("vehicle.productOriginals.unavailable")}
              />
            ))}
          </div>
        )}
        <DialogFooter className="justify-center border-t px-4 py-3 sm:justify-center">
          <DialogClose asChild>
            <Button type="button" size="xs">
              {t("vehicle.productOriginals.close")}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
